<template>
    <section
        class="chat_file_upload"
        role="region"
        aria-labelledby="file-upload-title"
    >
        <header class="chat_file_upload__header">
            <button
                type="button"
                class="chat_file_upload__close"
                aria-label="Cancel file upload"
                title="Cancel upload"
                @click="cancelUpload"
            >
                <i class="fa fa-times" aria-hidden="true" />
            </button>

            <span class="chat_file_upload__file_icon" aria-hidden="true">
                <i class="fa fa-paperclip" />
            </span>

            <span class="chat_file_upload__details">
                <strong id="file-upload-title" :title="displayFilename">
                    {{ displayFilename }}
                </strong>

                <small>
                    {{ fileTypeLabel }}
                    <template v-if="formattedFileSize">
                        <span aria-hidden="true">&bull;</span>
                        {{ formattedFileSize }}
                    </template>
                </small>
            </span>

            <span
                class="chat_file_upload__status"
                :class="{
                    'chat_file_upload__status--uploading': submitted
                }"
            >
                <i
                    class="fa"
                    :class="submitted ? 'fa-spinner fa-spin' : 'fa-check'"
                    aria-hidden="true"
                />
                {{ submitted ? 'Uploading' : 'Ready' }}
            </span>
        </header>

        <main class="chat_file_upload__preview">
            <div
                class="chat_file_upload__preview_card"
                :class="{
                    'chat_file_upload__preview_card--image':
                        canShowImagePreview
                }"
            >
                <img
                    v-if="canShowImagePreview"
                    :src="imageSrc"
                    :alt="'Preview of ' + displayFilename"
                    class="chat_file_upload__image"
                    @error="handlePreviewError"
                >

                <div v-else class="chat_file_upload__document">
                    <span class="chat_file_upload__document_icon">
                        <img
                            v-if="previewIcon"
                            :src="previewIcon"
                            alt=""
                            @error="handleFileIconError"
                        >

                        <i
                            v-else
                            class="fa fa-file-o"
                            aria-hidden="true"
                        />
                    </span>

                    <strong :title="displayFilename">
                        {{ displayFilename }}
                    </strong>
                    <small>{{ documentDescription }}</small>
                </div>
            </div>
        </main>

        <footer class="chat_file_upload__footer">
            <form
                class="chat_file_upload__form"
                enctype="multipart/form-data"
                novalidate
                @submit.prevent="submitUpload"
            >
                <suggestion-user-list
                    v-if="canShowSuggestionList"
                    v-model="suggestionDataModel"
                    v-click-outside="closeSuggestionList"
                    :selectedGroupID="selectedRoomId"
                    :messageContentDraftVAL="value"
                    @select="handleSuggestionSelected"
                    @close="closeSuggestionList"
                />

                <div
                    class="chat_file_upload__composer"
                    :class="{
                        'chat_file_upload__composer--disabled': submitted
                    }"
                >
                    <!-- <span
                        class="chat_file_upload__caption_icon"
                        aria-hidden="true"
                    >
                        <i class="fa fa-comment-o" />
                    </span> -->

                    <ChatMessageTextarea
                        ref="fileMessageInput"
                        :value="value"
                        input-id="textarea_for_file_input"
                        input-name="fileMessage"
                        aria-label="File caption"
                        placeholder="Add a caption..."
                        :disabled="submitted"
                        :autofocus="true"
                        :min-height="24"
                        :max-height="96"
                        class="chat_file_upload__textarea"
                        @input="handleInput"
                        @keydown="handleKeydown"
                    />

                    <button
                        type="submit"
                        class="chat_file_upload__send"
                        :disabled="!canSubmit"
                        :aria-label="submitted ? 'Uploading file' : 'Send file'"
                        :title="submitted ? 'Uploading file' : 'Send file'"
                    >
                        <i
                            class="fa"
                            :class="submitted ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </form>
        </footer>
    </section>
</template>

<script>
import {
    checkFileExtension
} from '@/utils/chatFile';
import ChatMessageTextarea from
    '@/components/private/workspace/common/ChatMessageTextarea';

const SuggestionUserList = () => import(
    './SuggestionUserList.vue'
);

const IMAGE_EXTENSION_PATTERN =
    /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;

export default {
    name: 'ChatFileUpload',

    components: {
        ChatMessageTextarea,
        'suggestion-user-list': SuggestionUserList
    },

    props: {
        uploadFile: {
            default: null,
            validator(value) {
                return value === null || typeof value === 'object';
            }
        },

        filename: {
            type: String,
            default: ''
        },

        imageSrc: {
            type: String,
            default: ''
        },

        fileIcon: {
            type: String,
            default: ''
        },

        selectedRoomInfo: {
            type: Object,
            default: null
        },

        selectedRoomId: {
            type: [String, Number],
            default: ''
        },

        value: {
            type: String,
            default: ''
        },

        suggestionListVisible: {
            type: Boolean,
            default: false
        },

        suggestionUserData: {
            type: Object,
            default() {
                return {
                    handle: ''
                };
            }
        },

        submitted: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            previewFailed: false,
            fileIconFailed: false
        };
    },

    computed: {
        isGroupChat() {
            return Boolean(
                this.selectedRoomInfo &&
                this.selectedRoomInfo.is_group_chat
            );
        },

        hasSelectedRoomId() {
            return (
                this.selectedRoomId !== null &&
                this.selectedRoomId !== undefined &&
                this.selectedRoomId !== ''
            );
        },

        canShowSuggestionList() {
            return (
                this.isGroupChat &&
                this.hasSelectedRoomId &&
                this.suggestionListVisible
            );
        },

        displayFilename() {
            return String(
                this.filename ||
                (this.uploadFile && this.uploadFile.name) ||
                'Attachment'
            );
        },

        fileExtension() {
            const cleanName = this.displayFilename
                .split(/[?#]/)[0];
            const lastDotIndex = cleanName.lastIndexOf('.');

            if (
                lastDotIndex === -1 ||
                lastDotIndex === cleanName.length - 1
            ) {
                return '';
            }

            return cleanName
                .slice(lastDotIndex + 1)
                .toUpperCase();
        },

        fileTypeLabel() {
            if (this.fileExtension) {
                return `${this.fileExtension} file`;
            }

            const mimeType = this.uploadFile &&
                this.uploadFile.type;

            if (mimeType && mimeType.indexOf('/') !== -1) {
                return `${mimeType.split('/')[1].toUpperCase()} file`;
            }

            return 'File';
        },

        formattedFileSize() {
            const size = Number(
                this.uploadFile && this.uploadFile.size
            );

            if (!Number.isFinite(size) || size <= 0) {
                return '';
            }

            return this.formatFileSize(size);
        },

        isImageUpload() {
            const mimeType = String(
                (this.uploadFile && this.uploadFile.type) || ''
            );

            return (
                mimeType.indexOf('image/') === 0 ||
                IMAGE_EXTENSION_PATTERN.test(this.displayFilename)
            );
        },

        canShowImagePreview() {
            return Boolean(
                this.isImageUpload &&
                this.imageSrc &&
                !this.previewFailed
            );
        },

        previewIcon() {
            if (this.fileIconFailed) {
                return '';
            }

            return this.fileIcon ||
                checkFileExtension(this.displayFilename) ||
                '';
        },

        documentDescription() {
            const parts = [
                this.fileTypeLabel,
                this.formattedFileSize
            ].filter(Boolean);

            return parts.join(' · ');
        },

        canSubmit() {
            return Boolean(this.uploadFile && !this.submitted);
        },

        suggestionDataModel: {
            get() {
                return this.suggestionUserData;
            },

            set(value) {
                this.$emit('update:suggestion-user-data', value);
            }
        }
    },

    watch: {
        imageSrc() {
            this.previewFailed = false;
        },

        filename() {
            this.previewFailed = false;
            this.fileIconFailed = false;
        }
    },

    mounted() {
        window.addEventListener('keydown', this.handleWindowKeydown);
        this.focusInput();
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.handleWindowKeydown);
    },

    methods: {
        checkFileExtension,

        formatFileSize(bytes) {
            if (bytes < 1024) {
                return `${bytes} B`;
            }

            const units = ['KB', 'MB', 'GB', 'TB'];
            let value = bytes / 1024;
            let unitIndex = 0;

            while (value >= 1024 && unitIndex < units.length - 1) {
                value /= 1024;
                unitIndex += 1;
            }

            const precision = value >= 10 ? 0 : 1;

            return `${value.toFixed(precision)} ${units[unitIndex]}`;
        },

        handleInput(value, event) {
            this.$emit('input', value);
            this.$emit('message-input', event);
        },

        handleKeydown(event) {
            this.$emit('keydown', event);
        },

        submitUpload() {
            if (!this.canSubmit) {
                return;
            }

            this.$emit('submit');
        },

        cancelUpload() {
            this.$emit('cancel');
        },

        handleWindowKeydown(event) {
            if (!event || event.key !== 'Escape') {
                return;
            }

            if (this.suggestionListVisible) {
                this.closeSuggestionList();
                return;
            }

            if (!this.submitted) {
                this.cancelUpload();
            }
        },

        handlePreviewError() {
            this.previewFailed = true;
        },

        handleFileIconError() {
            this.fileIconFailed = true;
        },

        closeSuggestionList() {
            this.$emit('suggestion-close');
        },

        handleSuggestionSelected(user) {
            if (!user) {
                return;
            }

            const rawHandle =
                user.handle ||
                user.login ||
                user.username;

            if (!rawHandle) {
                return;
            }

            const normalizedHandle = String(rawHandle)
                .trim()
                .replace(/^@+/, '');

            if (!normalizedHandle) {
                return;
            }

            const mention = `@${normalizedHandle}`;
            const currentMessage = String(this.value || '');
            const input = this.getInputElement();

            let cursorPosition = input &&
                typeof input.selectionStart === 'number'
                ? input.selectionStart
                : currentMessage.length;

            cursorPosition = Math.max(
                0,
                Math.min(cursorPosition, currentMessage.length)
            );

            let textBeforeCursor = currentMessage.slice(
                0,
                cursorPosition
            );
            let textAfterCursor = currentMessage.slice(cursorPosition);
            let mentionStartIndex = textBeforeCursor.lastIndexOf('@');

            if (
                mentionStartIndex === -1 &&
                currentMessage.lastIndexOf('@') !== -1
            ) {
                cursorPosition = currentMessage.length;
                textBeforeCursor = currentMessage;
                textAfterCursor = '';
                mentionStartIndex = textBeforeCursor.lastIndexOf('@');
            }

            let messagePrefix = textBeforeCursor;

            if (mentionStartIndex !== -1) {
                messagePrefix = textBeforeCursor.slice(0, mentionStartIndex);
            } else if (messagePrefix && !/\s$/.test(messagePrefix)) {
                messagePrefix += ' ';
            }

            const leadingSpaceMatch = textAfterCursor.match(/^[ \t]+/);
            const separator = leadingSpaceMatch ? '' : ' ';
            const updatedMessage =
                messagePrefix +
                mention +
                separator +
                textAfterCursor;
            const nextCursorPosition =
                messagePrefix.length +
                mention.length +
                (
                    leadingSpaceMatch
                        ? leadingSpaceMatch[0].length
                        : separator.length
                );

            this.$emit('input', updatedMessage);
            this.$emit('suggestion-select', user);

            this.$nextTick(() => {
                const messageInput = this.getInputElement();

                if (!messageInput) {
                    return;
                }

                messageInput.focus();
                messageInput.setSelectionRange(
                    nextCursorPosition,
                    nextCursorPosition
                );
            });
        },

        focusInput() {
            const inputComponent = this.$refs.fileMessageInput;

            if (
                inputComponent &&
                typeof inputComponent.focus === 'function'
            ) {
                inputComponent.focus();
            }
        },

        getInputElement() {
            const inputComponent = this.$refs.fileMessageInput;

            if (
                inputComponent &&
                typeof inputComponent.getInputElement === 'function'
            ) {
                return inputComponent.getInputElement();
            }

            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
$background: #111111;
$surface: #181818;
$surface-raised: #202020;
$surface-hover: #252525;
$border: rgba(255, 255, 255, 0.04);
$text: #f5f5f5;
$muted: #9d9d9d;
$yellow: #ffd400;
$blue: #70c8ff;

.chat_file_upload {
    position: absolute;
    inset: 0;
    top: 60px;
    border-top:1px solid $border;
    z-index: 999;
    display: flex;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    flex-direction: column;
    color: $text;
    background: $background;
    font-family:
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        'Helvetica Neue',
        Arial,
        sans-serif;
    isolation: isolate;
}

.chat_file_upload,
.chat_file_upload *,
.chat_file_upload *::before,
.chat_file_upload *::after {
    box-sizing: border-box;
}

// .chat_file_upload button:focus-visible,
// .chat_file_upload textarea:focus-visible {
//     outline: 2px solid rgba(112, 200, 255, 0.85);
//     outline-offset: 2px;
// }

.chat_file_upload__header {
    position: relative;
    z-index: 3;
    display: flex;
    min-height: 62px;
    padding: 9px 16px;
    // border-bottom: 1px solid $border;
    align-items: center;
    gap: 11px;
    flex: 0 0 62px;
    background: $background;
}

.chat_file_upload__close,
.chat_file_upload__send {
    display: inline-flex;
    padding: 0;
    border: 0;
    align-items: center;
    justify-content: center;
    font: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.chat_file_upload__close {
    width: 36px;
    height: 36px;
    border: 1px solid $border;
    border-radius: 9px;
    flex: 0 0 36px;
    color: $muted;
    background: $surface;
    transition:
        color 160ms ease,
        border-color 160ms ease,
        background-color 160ms ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(255, 77, 69, 0.55);
        color: #ff4d45;
        background: rgba(255, 77, 69, 0.08);
    }
}

.chat_file_upload__file_icon {
    display: inline-flex;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    align-items: center;
    justify-content: center;
    flex: 0 0 34px;
    color: $yellow;
    background: rgba(255, 212, 0, 0.09);
    font-size: 15px;
}

.chat_file_upload__details {
    display: block;
    min-width: 0;
    flex: 1 1 auto;

    strong,
    small {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        color: $text;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.25;
    }

    small {
        margin-top: 3px;
        color: $muted;
        font-size: 11px;
        line-height: 1.2;

        span {
            margin: 0 4px;
            color: #555555;
        }
    }
}

.chat_file_upload__status {
    display: inline-flex;
    min-height: 28px;
    padding: 0 9px;
    border: 1px solid rgba(62, 199, 121, 0.25);
    border-radius: 14px;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    color: #4ed58a;
    background: rgba(62, 199, 121, 0.07);
    font-size: 11px;
    font-weight: 600;

    &--uploading {
        border-color: rgba(112, 200, 255, 0.28);
        color: $blue;
        background: rgba(112, 200, 255, 0.08);
    }
}

.chat_file_upload__preview {
    display: flex;
    min-height: 0;
    padding: 20px;
    overflow: auto;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    background:
        linear-gradient(rgba(255, 255, 255, 0.018) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.018) 1px, transparent 1px),
        $background;
    background-size: 24px 24px;
    overscroll-behavior: contain;
}

.chat_file_upload__preview_card {
    display: flex;
    width: 100%;
    max-width: 760px;
    height: 100%;
    max-height: 540px;
    min-height: 220px;
    overflow: hidden;
    border: 1px solid $border;
    border-radius: 14px;
    align-items: center;
    justify-content: center;
    background: $surface;
    box-shadow: 0 16px 44px rgba(0, 0, 0, 0.28);

    &--image {
        background:
            linear-gradient(45deg, #171717 25%, transparent 25%),
            linear-gradient(-45deg, #171717 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #171717 75%),
            linear-gradient(-45deg, transparent 75%, #171717 75%),
            #1b1b1b;
        background-position:
            0 0,
            0 8px,
            8px -8px,
            -8px 0;
        background-size: 16px 16px;
    }
}

.chat_file_upload__image {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    padding: 10px;
    object-fit: contain;
    object-position: center;
}

.chat_file_upload__document {
    display: flex;
    width: 100%;
    max-width: 380px;
    padding: 28px 22px;
    align-items: center;
    flex-direction: column;
    text-align: center;

    > strong,
    > small {
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    > strong {
        margin-top: 16px;
        color: $text;
        font-size: 15px;
        font-weight: 600;
    }

    > small {
        margin-top: 5px;
        color: $muted;
        font-size: 12px;
    }
}

.chat_file_upload__document_icon {
    display: inline-flex;
    width: 88px;
    height: 88px;
    border: 1px solid $border;
    border-radius: 18px;
    align-items: center;
    justify-content: center;
    color: $blue;
    background: $surface-raised;
    font-size: 40px;

    img {
        display: block;
        max-width: 54px;
        max-height: 54px;
        object-fit: contain;
    }
}

.chat_file_upload__footer {
    position: relative;
    z-index: 4;
    padding: 12px 16px 10px;
    // border-top: 1px solid $border;
    flex: 0 0 auto;
    background: $background;
}

.chat_file_upload__form {
    position: relative;
    width: 100%;
    max-width: 920px;
    margin: 0 auto;
}

.chat_file_upload__composer {
    display: flex;
    width: 100%;
    min-height: 52px;
    padding: 7px 7px 7px 13px;
    align-items: flex-end;
    gap: 7px;
    background:#000000;
    transition:
        border-color 160ms ease,
        box-shadow 160ms ease,
        background-color 160ms ease;
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 35px;
    box-shadow: 0 1px 8px -1px rgba(162, 169, 176, .6);

    &:focus-within,
    &:hover {
        border-color: #ffc107;
        // box-shadow:
        //     0 7px 24px rgba(0, 0, 0, 0.3),
        //     0 0 0 3px rgba(255, 212, 0, 0.06);
    }

    &--disabled {
        opacity: 0.75;
    }
}

.chat_file_upload__caption_icon {
    display: inline-flex;
    width: 26px;
    height: 38px;
    align-items: center;
    justify-content: center;
    flex: 0 0 26px;
    color: $muted;
    font-size: 16px;
}

.chat_file_upload__textarea {
    min-width: 0;
    flex: 1 1 auto;
}

.chat_file_upload__send {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    flex: 0 0 35px;
    color: $background;
    background: $yellow;
    font-size: 15px;
    transition:
        color 160ms ease,
        background-color 160ms ease,
        transform 160ms ease,
        opacity 160ms ease;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
        background: #ffe24a;
        transform: translateY(-1px);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        color: #737373;
        background: #292929;
        cursor: not-allowed;
        opacity: 0.75;
    }
}

.chat_file_upload__caption_hint {
    display: block;
    margin-top: 5px;
    padding-left: 16px;
    color: #707070;
    font-size: 10px;
    line-height: 1.2;
}

@media (max-width: 767px) {
    .chat_file_upload{top: 52px;}
    .chat_file_upload__header {
        min-height: 58px;
        padding: 8px 10px;
        gap: 8px;
        flex-basis: 58px;
    }

    .chat_file_upload__close {
        width: 34px;
        height: 34px;
        flex-basis: 34px;
    }

    .chat_file_upload__file_icon {
        display: none;
    }

    .chat_file_upload__status {
        width: 9px;
        min-height: 9px;
        padding: 0;
        overflow: hidden;
        border: 0;
        border-radius: 50%;
        color: transparent;
        background: #4ed58a;
        font-size: 0;

        .fa {
            display: none;
        }

        &--uploading {
            background: $blue;
        }
    }

    .chat_file_upload__preview {
        padding: 12px;
    }

    .chat_file_upload__preview_card {
        min-height: 180px;
        border-radius: 11px;
    }

    .chat_file_upload__image {
        padding: 6px;
    }

    .chat_file_upload__footer {
        padding: 10px 10px 8px;
    }

    .chat_file_upload__composer {
        min-height: 50px;
        padding: 5px 5px 5px 10px;
    }

    .chat_file_upload__caption_icon {
        display: none;
    }

    .chat_file_upload__send {
        width: 38px;
        height: 38px;
        flex-basis: 38px;
    }
}

@media (prefers-reduced-motion: reduce) {
    .chat_file_upload *,
    .chat_file_upload *::before,
    .chat_file_upload *::after {
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
    }
}
</style>
